<template>
  <div>
    <div v-if="title" class="text-subtitle-2 font-weight-medium text-medium-emphasis mb-2">{{ title }}</div>
    <v-row dense>
      <v-col cols="12" sm="6" md="2">
        <v-card flat rounded="lg" class="gap-backtest-card stat-tile">
          <v-card-text class="px-5 py-4">
            <div class="d-flex align-center justify-space-between">
              <div class="text-caption text-medium-emphasis">Win rate</div>
              <v-chip size="x-small" variant="tonal" :color="confidenceColor(stats.confidenceTier)">
                {{ confidenceLabel(stats.confidenceTier) }}
              </v-chip>
            </div>
            <div class="stat-value">{{ formatPct(stats.winRate.rawWinRatePct) }}</div>
            <div class="text-caption text-medium-emphasis mt-1">
              {{ stats.winRate.won }}W / {{ stats.winRate.lost }}L · Wilson LB {{ formatPct(stats.winRate.wilsonLowerBoundPct) }}
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" md="2">
        <v-card flat rounded="lg" class="gap-backtest-card stat-tile">
          <v-card-text class="px-5 py-4">
            <div class="text-caption text-medium-emphasis">Profit factor</div>
            <div class="stat-value" :class="isFiniteNumber(stats.profitFactor.profitFactor) ? (stats.profitFactor.profitFactor >= 1 ? 'text-success' : 'text-error') : ''">
              {{ isFiniteNumber(stats.profitFactor.profitFactor) ? stats.profitFactor.profitFactor.toFixed(2) : '—' }}
            </div>
            <div class="text-caption text-medium-emphasis mt-1">
              <template v-if="!isFiniteNumber(stats.profitFactor.profitFactor)">No losing trades yet</template>
              <template v-else>Gross win {{ formatMoney(stats.profitFactor.grossProfitRs) }} / loss {{ formatMoney(stats.profitFactor.grossLossRs) }}</template>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" md="2">
        <v-card flat rounded="lg" class="gap-backtest-card stat-tile">
          <v-card-text class="px-5 py-4">
            <div class="text-caption text-medium-emphasis">Expectancy / trade</div>
            <div class="stat-value" :class="isFiniteNumber(stats.expectancy.expectancyRs) ? (stats.expectancy.expectancyRs >= 0 ? 'text-success' : 'text-error') : ''">
              {{ formatMoney(stats.expectancy.expectancyRs) }}
            </div>
            <div class="text-caption text-medium-emphasis mt-1">
              Avg win {{ formatMoney(stats.expectancy.avgWinRs) }} · avg loss {{ formatMoney(-stats.expectancy.avgLossRs) }}
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col v-if="stats.holdTime" cols="12" sm="6" md="2">
        <v-card flat rounded="lg" class="gap-backtest-card stat-tile">
          <v-card-text class="px-5 py-4">
            <div class="text-caption text-medium-emphasis">Avg hold time</div>
            <div class="stat-value">{{ formatHoldMinutes(stats.holdTime.avgMinutes) }}</div>
            <div class="text-caption text-medium-emphasis mt-1">
              Longest {{ formatHoldMinutes(stats.holdTime.maxMinutes) }} · shortest {{ formatHoldMinutes(stats.holdTime.minMinutes) }}
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" md="2">
        <v-card flat rounded="lg" class="gap-backtest-card stat-tile">
          <v-card-text class="px-5 py-4">
            <div class="text-caption text-medium-emphasis">Max drawdown</div>
            <div class="stat-value text-error">{{ formatMoney(stats.drawdown.maxDrawdownRs) }}</div>
            <div class="text-caption text-medium-emphasis mt-1">
              {{ isFiniteNumber(stats.drawdown.maxDrawdownPct) ? `${stats.drawdown.maxDrawdownPct.toFixed(1)}% off peak` : 'From ₹0 baseline' }}
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" md="2">
        <v-card flat rounded="lg" class="gap-backtest-card stat-tile">
          <v-card-text class="px-5 py-4">
            <div class="text-caption text-medium-emphasis">Longest streak</div>
            <div class="stat-value text-success">{{ stats.streaks.longestWinStreak }}W</div>
            <div class="text-caption text-medium-emphasis mt-1">
              Longest losing streak {{ stats.streaks.longestLossStreak }}L
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" md="2">
        <v-card flat rounded="lg" class="gap-backtest-card stat-tile">
          <v-card-text class="px-5 py-4">
            <div class="text-caption text-medium-emphasis">Total P&amp;L, {{ stats.closedCount }} trade{{ stats.closedCount === 1 ? '' : 's' }}</div>
            <div class="stat-value" :class="stats.totalPnlRs >= 0 ? 'text-success' : 'text-error'">
              {{ formatMoney(stats.totalPnlRs) }}
            </div>
            <div class="text-caption text-medium-emphasis mt-1" v-if="showFormulaBreakCaveat">
              Spans the {{ stats.formulaBreakDate }} charge-formula change — see note below
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script>
// Shared stat-tile grid for "Dashboard Statistics" -- win rate, profit
// factor, expectancy, (optional) avg hold time, max drawdown, longest
// streak, and total P&L. Used by Day Review's Analytics tab (real closed
// PaperTrade rows) and History tab (backtest-derived rows, via
// buildDashboardStats() in day-review.controllers.js) so both read the same
// numbers off the same shape instead of two copies of this markup drifting
// apart. `stats.holdTime` is optional -- backtest-derived data has no
// wall-clock timestamps, so that tile is simply omitted when absent, rather
// than rendering a fabricated "no data" placeholder.
export default {
  name: 'TradeStatsTiles',
  props: {
    stats: { type: Object, required: true },
    title: { type: String, default: '' },
    showFormulaBreakCaveat: { type: Boolean, default: false },
  },
  methods: {
    isFiniteNumber(value) {
      return value !== null && value !== undefined && Number.isFinite(Number(value));
    },
    formatPct(value) {
      return this.isFiniteNumber(value) ? `${Number(value).toFixed(1)}%` : '—';
    },
    formatMoney(value) {
      return this.isFiniteNumber(value) ? this.$helper.formatPrice(Number(value)) : '—';
    },
    // Sample-size confidence, NOT a P&L judgment -- do not reuse win/loss colors here.
    // This tier is independent of pnlRs/winRatePct; a row can be 'experimental' (grey)
    // and strongly profitable, or 'reliable' (green) and a loser. See trade-analytics.js
    // for the tier thresholds -- this function only controls chip color, not the tier itself.
    confidenceColor(tier) {
      if (tier === 'reliable') return 'success';
      if (tier === 'limited') return 'grey';
      return 'grey-darken-1';
    },
    confidenceLabel(tier) {
      if (tier === 'reliable') return 'Reliable';
      if (tier === 'limited') return 'Limited data';
      return 'Experimental';
    },
    formatHoldMinutes(minutes) {
      if (!this.isFiniteNumber(minutes) || minutes < 0) return '—';
      const h = Math.floor(minutes / 60);
      const m = Math.round(minutes % 60);
      return h > 0 ? `${h}h ${m}m` : `${m}m`;
    },
  },
};
</script>
